import { useEffect, useState } from "react";
import styles from "./styles/FinancialChartPerEmployee.module.scss";
import DateRangePicker, { ValueType } from "rsuite/lib/DateRangePicker";
import dayjs from "dayjs";
import { Button, CheckPicker, FormGroup, SelectPicker, Notification, Loader, Input } from "rsuite";
import axios from "../../../../utilities/axios";
import { ParametersGlobals } from "../../../../core/parametersGlobals/ParametersGlobals";
import ReactApexChart from "react-apexcharts";

interface FinancialChartPerEmployeeProps {
    employeesData: { label: string; value: number }[]
}

export default function FinancialChartPerEmployee({ employeesData }: FinancialChartPerEmployeeProps) {
    //Filtros
    const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
    const [selectedErrors, setSelectedErrors] = useState<number[]>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<ValueType | undefined>([
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
    ]);

    //Utilidades
    const [loading, setLoading] = useState<boolean>(false);
    const [formattedErrorsData, setFormattedErrorsData] = useState<{ value: number, label: string }[]>([]);
    const [chartState, setChartState] = useState<any>(null);
    const [detailsChartState, setDetailsChartState] = useState<any>(null);
    const [detailErrorSelected, setDetailErrorSelected] = useState<number | null>(null);

    useEffect(() => {
        try {
            setLoading(true);

            fetchErrorsPerGroup();
        } catch (error) {
            console.log("Erro useEffect financialChartPerEmployee ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }, [])

    const fetchErrorsPerGroup = async () => {
        try {
            const response = await axios.get(`/api/v1/dashboard/chart/financial/custom/select-error-data`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID
                }
            });

            const filteredErrors = response.data
                .filter((error: any) => error.error_active === true)
                .map((error: any) => ({
                    value: error.error_id,
                    label: error.error_name
                }));

            const allErrorIds = filteredErrors.map((e: any) => e.value);

            setFormattedErrorsData(filteredErrors);
            setSelectedErrors(allErrorIds); // Marca todos por padrão
        } catch (error) {
            console.log("Erro financialChartPerEmployee.fetchErrorsPerGroup ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    };

    const clearFilters = () => {
        const allErrorIds = formattedErrorsData.map((e: any) => e.value);

        setSelectedErrors(allErrorIds); // Restaura todos os Lançamentos marcados
        setSelectedEmployee(null);
        setSelectedPeriod([
            dayjs().startOf('month').toDate(),
            dayjs().endOf('month').toDate(),
        ]);
        setChartState(null);
        setDetailErrorSelected(null);
        setDetailsChartState(null);
    };

    const fetchChartData = async () => {
        if (!selectedPeriod || !selectedEmployee) {
            Notification.warning({ title: 'Aviso', description: 'Selecione um período e um funcionário para visualizar os dados', placement: 'topEnd', close: true });
            return;
        }

        try {
            setLoading(true);
            setDetailErrorSelected(null)
            setDetailsChartState(null)

            const { data } = await axios.get(`/api/v1/dashboard/financial/secondary/chart/employee`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    employee: selectedEmployee,
                    errors: selectedErrors,
                    period: selectedPeriod
                }
            });

            const updatedChartOptions = {
                ...data.options,
                chart: {
                    ...(data.options.chart || {}),
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: function (event: any, chartContext: any, config: any) {
                            const index = config.dataPointIndex;
                            const clickedError = data.customData?.[index];

                            if (clickedError) {
                                // handleSearchDetails(clickedError);
                            }
                        }
                    }
                },
                tooltip: {
                    y: {
                        formatter: function () {
                            return ``;
                        },
                        title: {
                            formatter: (seriesName: any) => seriesName,
                        },
                    }
                }
            };

            setChartState({
                ...data,
                options: updatedChartOptions
            });
        } catch (error) {
            console.log("Erro financialChartPerEmployee.fetchChartData ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar dados', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }

    const handleSearchDetails = async (clickedError: any) => {
        try {
            setLoading(true);
            setDetailErrorSelected(clickedError.errorName);

            const { data } = await axios.get(`/api/v1/dashboard/financial/secondary/chart/employee/details`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    employee: selectedEmployee,
                    error: clickedError.errorId,
                    period: selectedPeriod
                }
            });

            setDetailsChartState(data.detailsChart);
        } catch (error) {
            console.log("Erro financialChartPerEmployee.handleSearchDetails ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar dados', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.containerFilters}>
                    <div>
                        <FormGroup>
                            <label>Período</label>
                            <DateRangePicker
                                placeholder="Selecione uma data"
                                format="DD/MM/YYYY"
                                ranges={[]}
                                value={selectedPeriod}
                                onChange={(value) => setSelectedPeriod(value)}
                                className={styles.customDateRangePicker}
                            />
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Funcionário</label>
                            <SelectPicker
                                data={employeesData}
                                placeholder="Selecione um funcionário..."
                                value={selectedEmployee}
                                onChange={(value) => setSelectedEmployee(value)}
                                className={styles.customSelectPicker}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Grupo de Lançamentos</label>
                            <Input
                                value="FINANCEIRO"
                                style={{ width: 150 }}
                                disabled
                            />
                            <span className={styles.informationText}>
                                * Obrigatório
                            </span>
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Lançamentos</label>
                            <CheckPicker
                                data={formattedErrorsData}
                                placeholder="Selecione os Lançamentos..."
                                cleanable
                                searchable
                                value={selectedErrors}
                                onChange={(value) => setSelectedErrors(value)}
                                className={styles.customSelectPicker}
                            />
                        </FormGroup>
                    </div>

                    <div>
                        <Button
                            color="cyan"
                            appearance="primary"
                            onClick={clearFilters}
                            loading={loading}
                            className={styles.customButton}
                        >
                            Limpar
                        </Button>

                        <Button
                            appearance="primary"
                            onClick={fetchChartData}
                            loading={loading}
                            className={styles.customButton}
                        >
                            Filtrar
                        </Button>
                    </div>
                </div>

                <div className={styles.containerCharts}>
                    {chartState ? (
                        <>
                            <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Por Funcionário</h5>

                                <ReactApexChart options={chartState?.options} series={chartState?.series} type="pie" height={500} />
                            </div>

                            {/* <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Detalhes {detailErrorSelected ? ` - ${detailErrorSelected}` : ""}</h5>

                                {loading ? (
                                    <div className={styles.noDataContainer} style={{ height: '90%', marginTop: '1rem' }}>
                                        <Loader size="md" content="Carregando detalhes..." />
                                    </div>
                                ) : (
                                    detailsChartState ? (
                                        <ReactApexChart options={detailsChartState?.options} series={detailsChartState?.series} type="pie" height={400} />
                                    ) : (
                                        <div className={styles.noDataContainer} style={{ height: '90%', marginTop: '1rem' }}>
                                            <p className={styles.noDataText}>Selecione um erro para visualizar os detalhes...</p>
                                        </div>
                                    )
                                )}
                            </div> */}
                        </>
                    ) : (
                        <div className={styles.noDataContainer}>
                            <p className={styles.noDataText}>Nenhum dado encontrado</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
